import {
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { CmpsInsightCoachingPerformanceData } from '@cmps/ng-kit';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { animationFrameScheduler, of } from 'rxjs';
import {
    auditTime,
    catchError,
    delay,
    retryWhen,
    scan,
    take,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { CoachingTrendData } from '../card--my-coaching-card/models/coaching-trend-data.model';
import { CmpsTrendViewDialogComponent } from '../card--my-coaching-card/trend-view-dialog/trend-view-dialog.component';
import { useSelector2 } from '../coaching/store-use-selector';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';
import { PerformanceDataViewType } from '../insight/performance-data/metric.pd.model';
import { StateToProps } from '../store';
import { useDispatcher, UseStore } from '../store.decorator';

import {
    CmpsUser,
    MetricPerformanceData,
} from './../../kit/insight/metric-performance/metric-performance.metadata';
import { UserPerformanceData } from './models/user-performance-data.model';
import {
    clearState,
    reload,
    selectPillarAction,
} from './my-performance-card.action';
import { TranslationKeyMap } from './my-performance-card.i18n';
import { performanceCardReducer } from './my-performance-card.reducer';
import { PerformanceCardState } from './my-performance-card.state';
import { CmpsPillarViewDialogComponent } from './pillar-view-dialog/pillar-view-dialog.component';
import { getMetricPerformanceData } from './state/metric-performance-data.action';
import { getPerformanceData } from './state/performance-data.action';
import { togglePillarCollapseAction } from './state/toggle.action';

@Component({
    selector: '[cmpsMyPerformanceCard]',
    templateUrl: './my-performance-card.component.html',
})
@TakeUntilDestroy()
@UseStore<PerformanceCardState>('performanceCard', performanceCardReducer)
export class MyPerformanceCardComponent
    implements OnInit, OnDestroy, StateToProps<PerformanceCardState>
{
    state: PerformanceCardState;

    /** @private */ _i18n = TranslationKeyMap;

    /** @private */ _isExpanded: boolean;
    /** @private */ _performanceDataViewType = PerformanceDataViewType;
    /** @private */ _dialogRef: CmpsDialogRef<
        CmpsPillarViewDialogComponent,
        any
    >;

    /** @private */ _dialogRefTrendView: CmpsDialogRef<
        CmpsTrendViewDialogComponent,
        any
    >;

    /** @private */
    private _pendingDialogKey: string;

    constructor(
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef<HTMLElement>,
    ) {
        this._isExpanded = false;
    }

    ngOnInit(): void {
        this._onReload();
        this._getPerformanceData();
        this._getMetricPerformanceData();
        this._getTrendViewDialogTrigger();
    }

    ngAfterViewInit(): void {}

    ngOnDestroy(): void {
        useDispatcher(this, clearState({}));
    }

    /** @private */
    _onExpandClick(): void {
        this._isExpanded = !this._isExpanded;
        const cardElement = document.getElementsByClassName(
            'my-performance-card',
        )[0].parentElement.classList;
        cardElement.toggle('insight-component-item--expanded');

        this._scrollIntoView('.my-performance-card');
    }

    /** @private */
    _onSelectPillar(
        event: CmpsInsightCoachingPerformanceData,
        isParentPillar: boolean,
    ): void {
        useDispatcher(
            this,
            selectPillarAction({
                userId: this._getLoggedInUserData()?.performanceCard?.userId,
                colorGroup: event.backgroundColor,
                isParentPillar,
            }),
        );
    }

    _onTogglePillar(id?: string, metricId?: number): void {
        useDispatcher(
            this,
            togglePillarCollapseAction({
                id,
                metricId,
                userId: this._getLoggedInUserData()?.performanceCard?.userId,
            }),
        );
    }

    _onLaunchSelect(event?: string | CmpsUser): void {
        const cardElement = this._el.nativeElement.querySelector(
            '.my-performance-card',
        ) as HTMLElement;
        const cardWidth = cardElement?.offsetWidth;

        let id: string;
        let user: CmpsUser;

        if (typeof event === 'string') {
            id = event;
        } else if (event != null && typeof event === 'object') {
            user = event;
        }
        user =
            user == null
                ? this.state.users.find((user) => user.isLoggedIn)
                : user;

        this._dialogRef = this._dialog.open(CmpsPillarViewDialogComponent, {
            canClose: () => true,
            data: { id, cardWidth, user },
            movable: true,
        });
    }

    /** @private */
    _onReload(): void {
        useDispatcher(
            this,
            reload({
                userId: this._authService.user.id,
                triggeredFromRoot: true,
            }),
        );
    }

    /** @private */
    _trendView(row: MetricPerformanceData): void {
        const user = row.user;
        const queueId =
            user == null && row.isChild === true ? row.secondaryId : null;
        const metricId = row.metricId;
        const userId =
            user != null
                ? user.userId
                : this._getLoggedInUserData()?.performanceCard?.userId;
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const key = `${userId}-${metricId ?? ''}-${queueId ?? ''}`;
        const chartData = this.state.trendCharts?.[key];

        if (chartData != null) {
            this._dialogRefTrendView = this._dialog.open(
                CmpsTrendViewDialogComponent,
                {
                    canClose: () => true,
                    data: chartData,
                    movable: true,
                },
            );
        } else {
            this._pendingDialogKey = key;

            const isAlreadyLoading = this.state.pending?.trendDataByKey?.[key];
            if (isAlreadyLoading == null) {
                useDispatcher(
                    this,
                    getPerformanceData({
                        userId,
                        metricId,
                        queueId,
                    }),
                );
            }
        }
    }

    /* @private */
    _getPerformanceData(): void {
        useSelector2<number | null>((state) => {
            const cardState = state?.performanceCard as PerformanceCardState;

            const trendChartsMissing =
                cardState?.trendCharts == null ||
                Object.keys(cardState.trendCharts).length === 0;

            if (!trendChartsMissing) return null;

            const loggedInUser = Object.values(cardState?.userData ?? {}).find(
                (user) => user.isLoggedIn,
            );

            return loggedInUser?.performanceCard?.userId ?? null;
        })
            .pipe(
                tap(([prevState, currState]) => {
                    if (prevState == null && currState != null) {
                        useDispatcher(
                            this,
                            getPerformanceData({ userId: currState }),
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /* @private */
    _getMetricPerformanceData(): void {
        useSelector2<number>((state) => {
            const userData =
                (state?.performanceCard as PerformanceCardState)?.userData ??
                {};

            const entry = Object.values(userData).find(
                (user) =>
                    user.metricPerformanceData == null ||
                    user.metricPerformanceData.length === 0,
            );

            return entry?.performanceCard?.userId;
        })
            .pipe(
                tap(([prev, curr]) => {
                    if (prev !== curr && curr != null) {
                        useDispatcher(
                            this,
                            getMetricPerformanceData({ userId: curr }),
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _getTrendViewDialogTrigger(): void {
        useSelector2<Record<string, CoachingTrendData>>(
            (state) => state?.performanceCard?.trendCharts ?? {},
        )
            .pipe(takeUntilDestroy(this))
            .subscribe(([prev, curr]) => {
                const key: string = this._pendingDialogKey;

                if (key !== '' && prev?.[key] == null && curr?.[key] != null) {
                    this._dialogRefTrendView = this._dialog.open(
                        CmpsTrendViewDialogComponent,
                        {
                            canClose: () => true,
                            data: curr[key],
                            movable: true,
                        },
                    );

                    this._pendingDialogKey = null;
                }
            });
    }

    private _getLoggedInUserData(): UserPerformanceData | undefined {
        if (this.state?.userData == null) {
            return undefined;
        }

        return Object.values(this.state.userData).find((u) => u?.isLoggedIn);
    }

    get _isTrendLoading(): boolean {
        const pending = this.state.pending?.trendDataByKey ?? {};
        return Object.values(pending).some((v) => v);
    }

    private _scrollIntoView(selector: string): void {
        this._ngZone.onStable
            .pipe(
                take(1),
                delay(1000),
                tap(() => {
                    const el = this._el.nativeElement.querySelector(
                        selector,
                    ) as HTMLElement;
                    if (el != null) {
                        throw new Error('not found');
                    }
                    this._scrollIn(el);
                }),
                retryWhen((errors) =>
                    errors.pipe(
                        scan((acc) => {
                            if (acc >= 5) {
                                throw new Error('still not found');
                            }
                            return acc + 1;
                        }, 0),
                        auditTime(0, animationFrameScheduler),
                    ),
                ),
                catchError(() => {
                    return of(null);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _scrollIn(element: HTMLElement): void {
        const rect = element.parentElement.getBoundingClientRect();
        const scrollTop =
            window.pageYOffset != null
                ? window.pageYOffset
                : document.documentElement.scrollTop;
        const offset = {
            top: rect.top + scrollTop,
        };

        const navbarHeight = document
            .querySelector('cmps-nav>.navbar.fixed-top')
            .getBoundingClientRect().height;
        const top = offset.top - navbarHeight - 17;
        try {
            window.scrollTo({
                left: 0,
                top,
                behavior: 'smooth',
            });
        } catch (e) {
            window.scrollTo(0, top);
        }
    }
}
